import React, { useState } from "react";
import {
  Box, Text, Input, Switch, Button, Slider, SliderTrack, 
  SliderFilledTrack, SliderThumb, VStack, HStack, Badge,
  Spinner, Flex, FormControl, FormLabel
} from "@chakra-ui/react";
import { useColorModeValue } from "@chakra-ui/react";

const AppearanceTab = ({
  settings, 
  isWidgetOpen, 
  handleWidgetToggle, 
  presets,
  activePreset,
  applyPreset,
  handleColorChange,
  handleFontSizeChange,
  handleAvatarChange,
  triggerFileInput,
  fileInputRef,
  handleFileUpload,
  setSettings,
  setActivePreset,
  updateSettingsUrl,
  accentColor,
  isUploading,
  setSaveStatus,
  setIsUploading // Make sure this prop is passed from parent
}) => {
  const textColor = useColorModeValue("gray.700", "white");
  const [localIsUploading, setLocalIsUploading] = useState(false);

  // Enhanced trigger function that immediately shows loading state
  const handleTriggerFileInput = () => {
    // Set loading state immediately on button click
    setIsUploading ? setIsUploading(true) : setLocalIsUploading(true);
    triggerFileInput();
  };
  
  // Enhanced file upload handler
  const enhancedFileUpload = (event) => {
    if (!event.target.files || event.target.files.length === 0) {
      // No file selected, reset loading state
      setIsUploading ? setIsUploading(false) : setLocalIsUploading(false);
      return;
    }
    
    // Call the original handler
    handleFileUpload(event);
  };

  // Use either the prop from parent or local state
  const showSpinner = isUploading || localIsUploading;

  return (
    <VStack spacing={{ base: 4, md: 6 }} align="stretch">
      {/* Widget Status Control */}
      <FormControl>
        <Flex justify="space-between" align="center" mb={1}>
          <FormLabel htmlFor="widget-status" fontSize="sm" fontWeight="medium" mb={0}>
            Widget Status
          </FormLabel>
          <Switch
            id="widget-status"
            isChecked={isWidgetOpen}
            onChange={handleWidgetToggle}
            colorScheme="green"
            size={{ base: "md", md: "md" }}
          />
        </Flex>
        <Text fontSize="xs" color="gray.500">
          {isWidgetOpen ? "Widget is visible" : "Widget is minimized"}
        </Text>
      </FormControl>

      {/* Theme Presets */}
      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={2}>Theme Presets</Text>
        <Flex 
          wrap="wrap" 
          gap={2} 
          mb={2}
        >
          {presets.map((preset) => (
            <Button
              key={preset.name}
              size={{ base: "xs", sm: "sm" }}
              bg={preset.color}
              color="white"
              _hover={{ opacity: 0.9 }}
              onClick={() => applyPreset(preset)}
              variant={activePreset === preset.name ? "solid" : "outline"}
              borderWidth={activePreset === preset.name ? 2 : 1}
            >
              {preset.name}
            </Button>
          ))}
        </Flex>
      </Box>

      {/* Primary Color Control */}
      <FormControl>
        <FormLabel fontSize="sm" fontWeight="medium" mb={2}>
          Primary Color
        </FormLabel>
        <Flex direction={{ base: "column", sm: "row" }} gap={2}>
          <Input
            type="color"
            value={settings.color || "#44b474"}
            onChange={handleColorChange}
            w={{ base: "100%", sm: "60px" }}
            h="36px"
            p={1}
            borderRadius="md"
            mb={{ base: 2, sm: 0 }}
          />
          <Input 
            value={settings.color || "#44b474"} 
            onChange={handleColorChange}
            placeholder="Hex color code" 
            size="sm"
            flex="1"
          />
        </Flex>
      </FormControl>

      {/* Font Size Control */}
      <FormControl>
        <Flex justify="space-between" align="center" mb={2}>
          <FormLabel fontSize="sm" fontWeight="medium" mb={0}>
            Font Size
          </FormLabel>
          <Badge colorScheme="green" fontSize={{ base: "xs", md: "sm" }}>
            {settings.fontSize || "14px"}
          </Badge>
        </Flex>
        <Slider
          value={parseInt(settings.fontSize || "14px", 10)}
          min={6}
          max={20}
          step={1}
          onChange={handleFontSizeChange}
          mb={2}
          aria-label="Font size slider"
        >
          <SliderTrack>
            <SliderFilledTrack bg={accentColor} />
          </SliderTrack>
          <SliderThumb boxSize={4} bg={accentColor} />
        </Slider>
        <Flex justify="space-between">
          <Text fontSize="xs" color="gray.500">Small</Text>
          <Text fontSize="xs" color="gray.500">Large</Text>
        </Flex>
      </FormControl>

      {/* Bot Avatar Control */}
      <FormControl>
        <FormLabel fontSize="sm" fontWeight="medium" mb={2}>
          Upload Logo
        </FormLabel>
        <VStack spacing={2} align="stretch">
          <Flex 
            direction={{ base: "column", sm: "row" }} 
            gap={2}
          >
            <Input
              placeholder="Enter avatar image URL"
              value={settings.botAvatar && !settings.botAvatar.startsWith('data:image') ? settings.botAvatar : ""}
              onChange={handleAvatarChange}
              size="sm"
              flex="1"
              mb={{ base: 2, sm: 0 }}
            />
            <Button
              onClick={handleTriggerFileInput}
              size="sm"
              colorScheme="blue"
              w={{ base: "100%", sm: "auto" }}
              isDisabled={showSpinner}
            >
              {showSpinner ? <Spinner size="sm" mr={2} /> : null}
              {showSpinner ? "Uploading..." : "Upload"}
            </Button>
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept="image/*"
              onChange={enhancedFileUpload}
            />
          </Flex>
          <Text fontSize="xs" color="gray.500">Enter URL or upload an image (max 2MB)</Text>
          
          {/* Avatar Preview */}
          {settings.botAvatar && (
            <Flex 
              mt={2} 
              align="center" 
              justify="space-between"
              direction={{ base: "column", sm: "row" }}
              gap={2}
            >
              <Flex align="center" gap={2}>
                <Box borderRadius="full" overflow="hidden" w="40px" h="40px" flexShrink={0}>
                  <img 
                    src={settings.botAvatar} 
                    alt="Bot avatar preview" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/40?text=Error';
                    }}
                  />
                </Box>
                <Text fontSize="xs" color="gray.500">Avatar preview</Text>
              </Flex>
              
              <Button 
                size="xs" 
                colorScheme="red" 
                variant="ghost"
                w={{ base: "100%", sm: "auto" }}
                onClick={() => {
                  const updatedSettings = { ...settings, botAvatar: "" };
                  setSettings(updatedSettings);
                  setActivePreset("custom");
                  localStorage.setItem('settings', JSON.stringify(updatedSettings));
                  updateSettingsUrl(updatedSettings);
                  setSaveStatus && setSaveStatus('Avatar removed');
                }}
              >
                Remove
              </Button>
            </Flex>
          )}
        </VStack>
      </FormControl>
    </VStack>
  );
};

export default AppearanceTab;